import Phaser from "phaser";
import {
  BUILDING_META,
  LOOT,
  sprintPaused,
  sprintReady,
  type BuildingId,
  type DecorationId,
} from "@engine";
import {
  BOARD,
  BUBBLE_SPOTS,
  DECO_SCALE,
  DECO_SPOTS,
  HUT_SPOTS,
  RAID_LAYOUT,
  WALL_HIT_LINES,
  WORLD,
  buildingArt,
  harbourArt,
} from "../../art/design/village.ts";
import { TEXTURES, placeArt } from "../design/textures.ts";
import {
  abortSprint,
  beginSprint,
  finishSprint,
  hydrateSession,
  purchase,
  togglePause,
  type Session,
} from "../state.ts";
import { bindHud } from "../ui/hud.ts";
import { bindShopTabs, closeShop, renderShop } from "../ui/shop.ts";
import { closeSprintModal, renderSprint } from "../ui/sprint.ts";
import { closePanel, renderBuildingPanel, renderScenicPanel } from "../ui/panel.ts";
import { morningState, showCollectBanner, showRaid, endRaid, markMorningDone } from "../ui/morning.ts";
import { $, hide, show } from "../ui/dom.ts";

/** Where a focused building sits on screen (share of height) and how far to zoom in. */
type Focus = { screenY: number; zoom: number; spot: boolean } | null;

const DECO_POS_KEY = "life-village-deco-positions-v1";
const TAP_SLOP = 10;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked (private mode): positions just reset next visit.
  }
}

export class VillageScene extends Phaser.Scene {
  private session: Session | null = null;
  private hud: ReturnType<typeof bindHud> | null = null;
  private buildings = new Map<BuildingId, Phaser.GameObjects.Image[]>();
  private decos = new Map<DecorationId, Phaser.GameObjects.Image>();
  private bubbles: Phaser.GameObjects.Image[] = [];
  private raidLayer: Phaser.GameObjects.Container | null = null;
  private goblins: Phaser.GameObjects.Image[] = [];
  private fitZoom = 1;
  private homeCenter = { x: BOARD.w / 2, y: BOARD.h / 2 };
  private focus: Focus = null;
  private focusTarget: { x: number; y: number } | null = null;
  private pinch = 0;
  private downAt: { x: number; y: number } | null = null;
  private draggingDeco = false;
  private toastTimer: number | null = null;

  public constructor() {
    super("village");
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#3e8f3a");
    this.hud = bindHud();
    this.drawGround();
    this.setupCamera();
    this.bindDom();

    void hydrateSession().then((session) => {
      this.session = session;
      this.drawBuildings();
      this.drawDecorations();
      this.refreshHud();
      this.startMorning();
      this.playLevelUps();
      const sprint = session.result.state.sprint;
      if (sprint && !sprint.completed) {
        this.openSprint();
      }
      (window as Window & { lifeVillage?: unknown }).lifeVillage = {
        openBuilding: (id: BuildingId) => this.openBuilding(id),
        openShop: () => this.openShop(),
        attack: () => this.onAttack(),
        camera: () => {
          const cam = this.cameras.main;
          return { x: cam.midPoint.x, y: cam.midPoint.y, zoom: cam.zoom, w: cam.width, h: cam.height };
        },
      };
    });

    this.time.addEvent({ delay: 250, loop: true, callback: () => this.tickSprint() });
  }

  /* ---------- World ---------- */

  private drawGround(): void {
    placeArt(this, "surround", 0, 0).setDepth(-2000);
    placeArt(this, "board-back", 0, 0).setDepth(-1000);
    placeArt(this, "board-front", 0, 0).setDepth(2000);

    const harbour = harbourArt();
    const boat = placeArt(this, "harbour", harbour.x, harbour.y).setDepth(harbour.y);
    this.makeTappable(boat, () => this.openScenic());
    this.tweens.add({ targets: boat, y: boat.y + 1.2, duration: 1600, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });

    for (const [x1, y1, x2, y2] of WALL_HIT_LINES) {
      const length = Phaser.Math.Distance.Between(x1, y1, x2, y2);
      const zone = this.add
        .zone((x1 + x2) / 2, (y1 + y2) / 2, length, 14)
        .setRotation(Math.atan2(y2 - y1, x2 - x1))
        .setDepth(2100);
      this.makeTappable(zone, () => this.openBuilding("walls"));
    }
    const gate = this.add.zone(195, 562, 44, 30).setDepth(2101);
    this.makeTappable(gate, () => this.openBuilding("walls"));
  }

  private drawBuildings(): void {
    if (!this.session) return;
    for (const list of this.buildings.values()) list.forEach((item) => item.destroy());
    this.buildings.clear();
    const levels = this.session.result.state.levels;

    for (const [id] of Object.entries(levels) as Array<[BuildingId, number]>) {
      const art = buildingArt(id);
      if (!art) continue;
      const spots = id === "builderHut" ? HUT_SPOTS : [{ x: art.x, y: art.y }];
      const images = spots.map((spot) => {
        const image = placeArt(this, `b-${id}`, spot.x, spot.y).setDepth(spot.y);
        this.makeTappable(image, () => this.openBuilding(id));
        return image;
      });
      this.buildings.set(id, images);
    }
  }


  private drawDecorations(): void {
    if (!this.session) return;
    const saved = readJson<Partial<Record<DecorationId, { x: number; y: number }>>>(DECO_POS_KEY, {});
    for (const id of this.session.result.state.decorations) {
      if (this.decos.has(id)) continue;
      const spot = saved[id] ?? DECO_SPOTS[id];
      const image = this.add.image(spot.x, spot.y, `d-${id}`);
      const info = TEXTURES.get(`d-${id}`);
      image.setOrigin(0.5, 0.9).setScale(DECO_SCALE / (info?.res ?? 1)).setDepth(spot.y);
      image.setInteractive({ useHandCursor: true, draggable: true, pixelPerfect: true, alphaTolerance: 40 });
      image.on("pointerdown", () => {
        this.draggingDeco = true;
      });
      image.on("drag", (_p: Phaser.Input.Pointer, x: number, y: number) => {
        image.setPosition(
          Phaser.Math.Clamp(x, 8, BOARD.w - 8),
          Phaser.Math.Clamp(y, 300, BOARD.h - 30),
        );
        image.setDepth(image.y);
      });
      image.on("dragend", () => {
        this.draggingDeco = false;
        const next = readJson<Record<string, { x: number; y: number }>>(DECO_POS_KEY, {});
        next[id] = { x: Math.round(image.x), y: Math.round(image.y) };
        writeJson(DECO_POS_KEY, next);
      });
      image.on("pointerup", () => {
        this.draggingDeco = false;
      });
      this.decos.set(id, image);
    }
  }

  private makeTappable(
    target: Phaser.GameObjects.Image | Phaser.GameObjects.Zone,
    onTap: () => void,
  ): void {
    if (target instanceof Phaser.GameObjects.Image) {
      target.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 30 });
    } else {
      target.setInteractive({ useHandCursor: true });
    }
    target.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      if (this.draggingDeco || !this.downAt) return;
      const moved = Phaser.Math.Distance.Between(this.downAt.x, this.downAt.y, pointer.x, pointer.y);
      if (moved > TAP_SLOP * this.dpr()) return;
      if (target instanceof Phaser.GameObjects.Image) this.bounce(target);
      onTap();
    });
  }

  /* ---------- Camera ---------- */

  private dpr(): number {
    return this.game.registry.get("dpr") ?? 1;
  }

  private setupCamera(): void {
    const cam = this.cameras.main;
    cam.setBounds(WORLD.x, WORLD.y, WORLD.w, WORLD.h);
    this.fitHome(true);
    this.scale.on("resize", () => {
      this.fitHome(true);
      if (this.focusTarget) this.applyFocus(false);
    });
    this.input.addPointer(2);

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.downAt = { x: pointer.x, y: pointer.y };
    });
    this.input.on("wheel", (_p: Phaser.Input.Pointer, _g: unknown, _dx: number, dy: number) => {
      if (this.focusTarget) return;
      cam.setZoom(this.clampZoom(cam.zoom * (1 - dy * 0.0012)));
    });
    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.focusTarget || this.draggingDeco) return;
      const down = this.input.manager.pointers.filter((item) => item.isDown);
      if (down.length >= 2) {
        const [a, b] = down;
        if (!a || !b) return;
        const dist = Phaser.Math.Distance.Between(a.x, a.y, b.x, b.y);
        if (this.pinch > 0) cam.setZoom(this.clampZoom(cam.zoom * (dist / this.pinch)));
        this.pinch = dist;
        return;
      }
      this.pinch = 0;
      if (!pointer.isDown) return;
      cam.scrollX -= (pointer.x - pointer.prevPosition.x) / cam.zoom;
      cam.scrollY -= (pointer.y - pointer.prevPosition.y) / cam.zoom;
    });
  }

  private clampZoom(zoom: number): number {
    return Phaser.Math.Clamp(zoom, this.fitZoom * 0.8, this.fitZoom * 2.8);
  }

  /**
   * Phones (portrait): the board fills the width, exactly like the design.
   * Wider screens: frame the walled village with forest around it.
   */
  private fitHome(instant: boolean): void {
    const cam = this.cameras.main;
    const w = cam.width;
    const h = cam.height;
    if (w / h < 0.75) {
      this.fitZoom = w / BOARD.w;
      this.homeCenter = { x: BOARD.w / 2, y: BOARD.h / 2 };
    } else {
      const focusW = 470;
      const focusH = 560;
      this.fitZoom = Math.min(w / focusW, h / focusH);
      this.homeCenter = { x: BOARD.w / 2, y: 440 };
    }
    if (instant && !this.focusTarget) {
      cam.setZoom(this.fitZoom);
      cam.centerOn(this.homeCenter.x, this.homeCenter.y);
    }
  }

  private goHome(): void {
    this.focusTarget = null;
    this.focus = null;
    const cam = this.cameras.main;
    cam.zoomTo(this.fitZoom, 420, "Sine.easeInOut", true);
    cam.pan(this.homeCenter.x, this.homeCenter.y, 420, "Sine.easeInOut", true);
    this.hideSpotlight();
  }

  /** Moves the camera so a world point sits at a share of the screen height. */
  private focusOn(x: number, y: number, screenY: number, zoomFactor: number, spot = true): void {
    this.focusTarget = { x, y };
    this.focus = { screenY, zoom: zoomFactor, spot };
    if (!spot) this.hideSpotlight();
    this.applyFocus(true);
  }

  private applyFocus(animate: boolean): void {
    if (!this.focusTarget || !this.focus) return;
    const cam = this.cameras.main;
    const zoom = this.fitZoom * this.focus.zoom;
    const viewH = cam.height / zoom;
    const centerX = this.focus.zoom > 1 ? this.focusTarget.x : cam.midPoint.x;
    const centerY = this.focusTarget.y + (0.5 - this.focus.screenY) * viewH;
    if (animate) {
      cam.zoomTo(zoom, 450, "Sine.easeInOut", true);
      cam.pan(centerX, centerY, 450, "Sine.easeInOut", true, (_c: unknown, progress: number) => {
        if (progress === 1) this.placeSpotlight();
      });
    } else {
      cam.setZoom(zoom);
      cam.centerOn(centerX, centerY);
      this.placeSpotlight();
    }
  }

  private placeSpotlight(): void {
    if (!this.focusTarget || !this.focus?.spot) return;
    const cam = this.cameras.main;
    const dpr = this.dpr();
    const sx = ((this.focusTarget.x - cam.worldView.x) * cam.zoom) / dpr;
    const sy = ((this.focusTarget.y - 22 - cam.worldView.y) * cam.zoom) / dpr;
    const radius = (58 * cam.zoom) / dpr;
    const spot = $("spotlight");
    spot.style.setProperty("--sx", `${sx}px`);
    spot.style.setProperty("--sy", `${sy}px`);
    spot.style.setProperty("--sr", `${radius}px`);
    show(spot);
  }

  private hideSpotlight(): void {
    hide($("spotlight"));
  }

  /* ---------- DOM wiring ---------- */

  private bindDom(): void {
    $("attack-btn").addEventListener("click", () => this.onAttack());
    $("shop-btn").addEventListener("click", () => this.openShop());
    $("panel-close").addEventListener("click", () => this.closeBuilding());
    $("panel-back").addEventListener("click", () => this.closeBuilding());
    $("shop-close").addEventListener("click", () => {
      closeShop();
      this.setLayer(null);
      this.goHome();
    });
    bindShopTabs();
    $("morning-collect").addEventListener("click", () => this.collect());
    $("raid-motivator").addEventListener("click", () => {
      this.finishRaid();
      this.openBuilding("builderHut");
    });
    $("raid-repair").addEventListener("click", () => {
      this.finishRaid();
      this.openShop();
    });
    $("sprint-close").addEventListener("click", () => {
      closeSprintModal();
      this.setLayer(null);
      this.goHome();
      this.refreshHud();
    });
    $("sprint-pause").addEventListener("click", () => {
      if (!this.session) return;
      this.session = togglePause(this.session, Date.now());
      renderSprint(this.session, Date.now());
      this.refreshHud();
    });
    $("sprint-cancel").addEventListener("click", () => {
      if (!this.session) return;
      this.session = abortSprint(this.session);
      closeSprintModal();
      this.setLayer(null);
      this.goHome();
      this.refreshHud();
      this.toast("Sprint cancelled. No loot this time.");
    });
  }

  /* ---------- Morning: collect or raid ---------- */

  private startMorning(): void {
    if (!this.session) return;
    const morning = morningState(this.session);
    if (morning.kind === "collect") {
      this.showBubbles();
      showCollectBanner();
    } else if (morning.kind === "raid") {
      this.showRaidLayer();
      showRaid(morning.raid);
      document.body.classList.add("raid-mode");
      this.refreshHud();
    }
  }

  private showBubbles(): void {
    const spots: Array<[string, { x: number; y: number }]> = [
      ["bubble-gold", BUBBLE_SPOTS.gold],
      ["bubble-crystal", BUBBLE_SPOTS.crystal],
    ];
    if (this.session?.metrics.dailyActionsDoneYesterday ?? true) {
      spots.push(["bubble-check", BUBBLE_SPOTS.check]);
    }
    this.bubbles = spots.map(([key, spot], index) => {
      const image = placeArt(this, key, spot.x, spot.y).setDepth(1500);
      this.tweens.add({
        targets: image,
        y: spot.y - 3,
        duration: 900,
        delay: index * 180,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
      image.setInteractive({ useHandCursor: true });
      image.on("pointerup", () => this.collect());
      return image;
    });
  }

  private collect(): void {
    if (!this.session) return;
    markMorningDone(this.session);
    hide($("morning-modal"));
    const collect = this.session.result.state.collect;
    for (const bubble of this.bubbles) {
      this.tweens.killTweensOf(bubble);
      this.flyToHud(bubble.texture.key === "bubble-crystal" ? "crystal" : "gold", bubble);
    }
    this.bubbles = [];
    if (collect?.happened) {
      this.toast(`+${collect.gold.toLocaleString("en-GB")} gold · +${collect.crystals.toLocaleString("en-GB")} crystals`);
    }
  }

  private flyToHud(kind: "gold" | "crystal", image: Phaser.GameObjects.Image): void {
    const cam = this.cameras.main;
    const target = document.querySelector<HTMLElement>(kind === "gold" ? ".resource.gold .res-icon" : ".resource.crystal .res-icon");
    const rect = target?.getBoundingClientRect();
    const dpr = this.dpr();
    const destX = rect ? cam.worldView.x + ((rect.left + rect.width / 2) * dpr) / cam.zoom : image.x;
    const destY = rect ? cam.worldView.y + ((rect.top + rect.height / 2) * dpr) / cam.zoom : image.y - 200;
    image.setDepth(3000);
    this.tweens.add({
      targets: image,
      x: destX,
      y: destY,
      scale: image.scale * 0.6,
      alpha: 0.4,
      duration: 650,
      ease: "Cubic.easeIn",
      onComplete: () => image.destroy(),
    });
  }

  private showRaidLayer(): void {
    const layer = this.add.container(0, 0).setDepth(2500);
    const tint = this.add.rectangle(WORLD.x, WORLD.y, WORLD.w, WORLD.h, 0x460812, 0.24).setOrigin(0);
    layer.add(tint);
    const put = (key: string, list: ReadonlyArray<readonly [number, number]>) =>
      list.map(([x, y]) => {
        const image = placeArt(this, key, x, y);
        layer.add(image);
        return image;
      });
    put("raid-rubble", RAID_LAYOUT.rubble);
    put("raid-smoke", RAID_LAYOUT.smoke).forEach((smoke, index) =>
      this.tweens.add({ targets: smoke, alpha: 0.55, y: smoke.y - 3, duration: 1100, delay: index * 200, yoyo: true, repeat: -1 }),
    );
    put("raid-fire", RAID_LAYOUT.fire).forEach((fire) =>
      this.tweens.add({ targets: fire, scaleY: fire.scaleY * 1.12, duration: 260, yoyo: true, repeat: -1 }),
    );
    this.goblins = put("raid-goblin", RAID_LAYOUT.goblins);
    this.goblins.forEach((goblin, index) =>
      this.tweens.add({ targets: goblin, y: goblin.y - 2.5, duration: 320, delay: index * 90, yoyo: true, repeat: -1, ease: "Quad.easeOut" }),
    );
    put("raid-sack", RAID_LAYOUT.sacks);
    this.raidLayer = layer;
  }

  private finishRaid(): void {
    if (this.session) markMorningDone(this.session);
    endRaid();
    document.body.classList.remove("raid-mode");
    const layer = this.raidLayer;
    if (layer) {
      // Goblins run off; smoke fades.
      for (const goblin of this.goblins) {
        this.tweens.killTweensOf(goblin);
        this.tweens.add({ targets: goblin, x: goblin.x < BOARD.w / 2 ? -40 : BOARD.w + 40, y: goblin.y + 60, duration: 900, ease: "Quad.easeIn" });
      }
      this.tweens.add({ targets: layer, alpha: 0, delay: 700, duration: 600, onComplete: () => layer.destroy() });
      this.raidLayer = null;
    }
    this.refreshHud();
  }

  /* ---------- Panels ---------- */

  /** Which full-screen layer is up; the HUD and morning banner step aside for it. */
  private setLayer(layer: "panel" | "shop" | "sprint" | null): void {
    document.body.classList.toggle("layer-panel", layer === "panel");
    document.body.classList.toggle("layer-shop", layer === "shop");
    document.body.classList.toggle("layer-sprint", layer === "sprint");
    for (const bubble of this.bubbles) bubble.setVisible(layer === null);
  }

  private openBuilding(id: BuildingId): void {
    if (!this.session) return;
    closeShop();
    this.setLayer("panel");
    renderBuildingPanel(this.session, id);
    const spot = this.buildingSpot(id);
    this.focusOn(spot.x, spot.y, 0.26, 1);
  }

  private openScenic(): void {
    this.setLayer("panel");
    renderScenicPanel("harbour");
    const harbour = harbourArt();
    this.focusOn(harbour.x, harbour.y, 0.26, 1);
  }

  private closeBuilding(): void {
    closePanel();
    this.setLayer(null);
    this.goHome();
  }

  private buildingSpot(id: BuildingId): { x: number; y: number } {
    if (id === "walls") return { x: 195, y: 560 };
    const art = buildingArt(id);
    return art ? { x: art.x, y: art.y } : { x: BOARD.w / 2, y: 450 };
  }

  private openShop(): void {
    if (!this.session) return;
    closePanel();
    this.hideSpotlight();
    this.focusTarget = null;
    this.setLayer("shop");
    renderShop(this.session, (itemId) => this.onBuy(itemId));
  }

  private onBuy(itemId: string): void {
    if (!this.session) return;
    const before = this.session.result.state;
    this.session = purchase(this.session, itemId);
    const after = this.session.result.state;
    if (after === before) {
      this.toast("Not enough loot for that yet.");
      return;
    }
    this.refreshHud();
    renderShop(this.session, (id) => this.onBuy(id));
    if (after.decorations.length > before.decorations.length) {
      this.drawDecorations();
      const added = after.decorations[after.decorations.length - 1];
      const image = added ? this.decos.get(added) : undefined;
      if (image) this.bounce(image);
      this.toast("Placed! Drag it anywhere on the grass.");
    } else if (after.wallHp !== before.wallHp) {
      this.toast(`Walls repaired to ${after.wallHp} HP.`);
    }
  }

  /* ---------- Attack (focus sprint) ---------- */

  private onAttack(): void {
    if (!this.session) return;
    const existing = this.session.result.state.sprint;
    if (!existing || existing.completed) {
      this.session = beginSprint(this.session, Date.now());
    }
    this.openSprint();
  }

  private openSprint(): void {
    if (!this.session) return;
    const sprint = this.session.result.state.sprint;
    if (!sprint || sprint.completed) return;
    closePanel();
    closeShop();
    this.setLayer("sprint");
    renderSprint(this.session, Date.now());
    const spot = this.buildingSpot(sprint.buildingId);
    this.focusOn(spot.x, spot.y, 0.29, 1.7, false);
    const images = this.buildings.get(sprint.buildingId);
    images?.forEach((image) => this.bounce(image));
  }

  private tickSprint(): void {
    const sprint = this.session?.result.state.sprint;
    if (!this.session || !sprint || sprint.completed) return;
    const now = Date.now();
    if (!$("sprint-modal").classList.contains("hidden")) {
      renderSprint(this.session, now);
    }
    this.hud?.setSprint(sprint, now);
    if (sprintPaused(sprint) || !sprintReady(sprint, now)) return;
    const target = sprint.buildingId;
    this.session = finishSprint(this.session, now);
    closeSprintModal();
    this.setLayer(null);
    this.goHome();
    this.refreshHud();
    const spot = this.buildingSpot(target);
    this.lootBurst(spot.x, spot.y - 30);
    this.toast(`Sprint done! +${LOOT.sprintGold} gold · +${LOOT.sprintCrystals} crystals`);
  }

  /* ---------- Effects ---------- */

  private refreshHud(): void {
    if (!this.session || !this.hud) return;
    const state = this.session.result.state;
    this.hud.setLevel(state.levels.townHall);
    this.hud.setLoot(state.gold, state.crystals);
    this.hud.setKeep(this.session.result.buildings.find((item) => item.id === "townHall"));
    this.hud.setWalls(state.wallHp);
    this.hud.setSprint(state.sprint, Date.now());
  }

  private playLevelUps(): void {
    for (const id of this.session?.result.state.justLeveled ?? []) {
      for (const image of this.buildings.get(id) ?? []) {
        this.bounce(image);
        image.setTintFill(0xffffff);
        this.time.delayedCall(160, () => image.clearTint());
      }
      this.toast(`${BUILDING_META[id].name} levelled up!`);
    }
  }

  private bounce(image: Phaser.GameObjects.Image): void {
    const base = TEXTURES.get(image.texture.key);
    const scale = (image.texture.key.startsWith("d-") ? DECO_SCALE : 1) / (base?.res ?? 1);
    this.tweens.killTweensOf(image);
    image.setScale(scale);
    this.tweens.add({ targets: image, scaleX: scale * 1.06, scaleY: scale * 0.94, duration: 90, yoyo: true, ease: "Quad.easeOut" });
  }

  private lootBurst(x: number, y: number): void {
    for (let i = 0; i < 8; i += 1) {
      const key = i % 2 === 0 ? "bubble-gold" : "bubble-crystal";
      const bit = placeArt(this, key, x, y, 0.6).setDepth(3000);
      this.tweens.add({
        targets: bit,
        x: x + Phaser.Math.Between(-40, 40),
        y: y - Phaser.Math.Between(30, 70),
        alpha: 0,
        duration: 700 + i * 50,
        ease: "Cubic.easeOut",
        onComplete: () => bit.destroy(),
      });
    }
  }

  private toast(message: string): void {
    const node = $("toast");
    node.textContent = message;
    show(node);
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => hide(node), 3200);
  }
}
